import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ThemePalette } from '@angular/material/core';
import { ProgressSpinnerMode } from '@angular/material/progress-spinner';
import { forkJoin } from 'rxjs';

import { AlertService } from './../../service/alert.service';
import { ClientesService } from './../../service/clientes.service';
import { GruposService } from './../../service/grupos.service';
import { PermissoesService } from './../../service/permissoes.service';

import { Cliente } from 'src/app/model/cliente.model';
import { Grupos } from './../../model/grupos.model';
import { Permissoes } from 'src/app/model/permissao.model';
import { VerificarPermissoes } from 'src/app/model/verificarPermissoes.model';

interface ProductSection {
  key: string;
  label: string;
  permissoes: Permissoes[];
}

@Component({
  selector: 'app-criar-grupo',
  templateUrl: './criar-grupo.component.html',
  styleUrls: ['./criar-grupo.component.css']
})
export class CriarGrupoComponent implements OnInit {
  color: ThemePalette = 'primary';
  mode: ProgressSpinnerMode = 'indeterminate';
  value = 50;
  load = true;

  form: FormGroup;
  clientes: Cliente[] = [];
  permissoes: Permissoes[] = [];

  productSections: ProductSection[] = [];
  activeSectionIndex = 0;
  permissionQuery = '';

  private selectedPermissionIds = new Set<string>();

  constructor(
    private formBuilder: FormBuilder,
    private router: Router,
    private service: GruposService,
    private servicePermissao: PermissoesService,
    private serviceClientes: ClientesService,
    private alertService: AlertService
  ) {
    this.form = this.formBuilder.group({
      vc_nome: new FormControl('', [Validators.required]),
      fk_cliente: new FormControl('', [Validators.required]),
    });
  }

  ngOnInit(): void {
    this.carregarDados();
  }

  get selectedPermissionCount(): number {
    return this.selectedPermissionIds.size;
  }

  onSectionIndexChange(index: number): void {
    this.activeSectionIndex = index;
    this.permissionQuery = '';
  }

  getSelectedCount(section: ProductSection): number {
    let count = 0;
    section.permissoes.forEach((p) => {
      if (p.sr_id != null && this.selectedPermissionIds.has(String(p.sr_id))) {
        count++;
      }
    });
    return count;
  }

  isSelected(id?: string): boolean {
    if (id == null) {
      return false;
    }
    return this.selectedPermissionIds.has(String(id));
  }

  onPermissionToggle(id: string | undefined, checked: boolean): void {
    if (id == null) {
      return;
    }

    const key = String(id);
    if (checked) {
      this.selectedPermissionIds.add(key);
    } else {
      this.selectedPermissionIds.delete(key);
    }
  }

  toggleAllInSection(section: ProductSection, checked: boolean): void {
    section.permissoes.forEach((p) => {
      if (p.sr_id == null) {
        return;
      }

      const key = String(p.sr_id);
      if (checked) {
        this.selectedPermissionIds.add(key);
      } else {
        this.selectedPermissionIds.delete(key);
      }
    });
  }

  getFilteredPermissions(section: ProductSection): Permissoes[] {
    const query = this.permissionQuery.trim().toLowerCase();
    if (!query) {
      return section.permissoes;
    }

    return section.permissoes.filter((p) => {
      const descricao = (p.vc_descricao ?? '').toLowerCase();
      const nome = (p.vc_nome ?? '').toLowerCase();
      return descricao.includes(query) || nome.includes(query);
    });
  }

  clearSearch(): void {
    this.permissionQuery = '';
  }

  formatPermissionText(text?: string): string {
    const value = String(text ?? '').trim();
    if (!value) {
      return '';
    }

    const lower = value.toLowerCase();
    return lower.charAt(0).toUpperCase() + lower.slice(1);
  }

  cadastrar(): void {
    if (!this.form.valid) {
      this.alertService.error('Preencha todos os campos.');
      return;
    }

    const ar_permissoes = this.getSelectedPermissionsForApi();
    if (ar_permissoes.length === 0) {
      this.alertService.error('É necessário selecionar ao menos uma permissão.');
      return;
    }

    const fk_cliente_raw = this.form.get('fk_cliente')?.value;
    const fk_cliente = this.toApiNumericId(fk_cliente_raw);

    const payload: Grupos = {
      vc_nome: this.form.get('vc_nome')?.value,
      fk_cliente,
      ar_permissoes,
    };

    this.service.cadastrarService(payload).subscribe({
      next: () => {
        this.alertService.success();
        this.router.navigate(['configuracao/grupos']);
      },
      error: () => {
        this.alertService.error('Preencha todos os campos.');
      }
    });
  }

  cancel(): void {
    this.router.navigate(['configuracao/grupos']);
  }

  public verificarPermissao(permissoesFuncionalidades: string[]): boolean {
    const permissaoUsuario = localStorage.getItem('permissoes');
    return VerificarPermissoes.temPermissao(permissoesFuncionalidades, permissaoUsuario);
  }

  trackBySectionKey(_: number, section: ProductSection): string {
    return section.key;
  }

  trackByPermissaoId(index: number, permissao: Permissoes): string {
    return String(permissao.sr_id ?? index);
  }

  private carregarDados(): void {
    this.load = true;

    forkJoin({
      permissoes: this.servicePermissao.listarService(),
      clientes: this.serviceClientes.listarServiceClientes(),
    }).subscribe({
      next: ({ permissoes, clientes }) => {
        this.permissoes = permissoes;
        this.clientes = clientes;
        this.productSections = this.buildProductSections(permissoes);
        this.activeSectionIndex = 0;
        this.permissionQuery = '';
        this.load = false;
      },
      error: () => {
        this.load = false;
      }
    });
  }

  private buildProductSections(permissoes: Permissoes[]): ProductSection[] {
    const canSeeRiana = this.verificarPermissao(['listar_permissoes_riana']);

    const byProduct = new Map<string, Permissoes[]>();
    permissoes.forEach((p) => {
      const produto = (p.produto ?? '').trim();
      const key = produto ? produto : 'Outros';

      if (key === 'Riana' && !canSeeRiana) {
        return;
      }

      const list = byProduct.get(key) ?? [];
      list.push(p);
      byProduct.set(key, list);
    });

    byProduct.forEach((list) => {
      list.sort((a, b) => (a.vc_descricao ?? '').localeCompare(b.vc_descricao ?? '', undefined, { sensitivity: 'base' }));
    });

    const preferredOrder = ['Premocenter', 'Hidrometer', 'Premoplan', 'Topes', 'Premovin', 'Cronos Dashboard', 'Riana', 'Outros'];
    const sections: ProductSection[] = Array.from(byProduct.entries()).map(([key, list]) => ({
      key,
      label: key,
      permissoes: list,
    }));

    sections.sort((a, b) => {
      const ia = preferredOrder.indexOf(a.key);
      const ib = preferredOrder.indexOf(b.key);
      const ra = ia === -1 ? 999 : ia;
      const rb = ib === -1 ? 999 : ib;
      if (ra !== rb) {
        return ra - rb;
      }
      return a.label.localeCompare(b.label);
    });

    return sections;
  }

  private getSelectedPermissionsForApi(): Array<number | string> {
    return Array.from(this.selectedPermissionIds)
      .map((id) => this.toApiPermissionId(id))
      .filter((id) => id !== '');
  }

  private toApiPermissionId(id: string): number | string {
    const value = String(id ?? '').trim();
    if (!value) {
      return '';
    }

    if (/^\d+$/.test(value)) {
      return Number(value);
    }

    return value;
  }

  private toApiNumericId(value: any): any {
    if (value == null) {
      return value;
    }

    if (typeof value === 'number') {
      return value;
    }

    const s = String(value).trim();
    if (/^\d+$/.test(s)) {
      return Number(s);
    }

    return value;
  }
}
